import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi'
import { z } from 'zod'
import { captureAndReturn } from '@omnea-app/packages/universal/zod-utils/captureAndReturn'

extendZodWithOpenApi(z)

export const OverrideIconTypeSchema = z.enum(['bank', 'budget-approval', 'assignees'])
export type OverrideIconType = z.infer<typeof OverrideIconTypeSchema>

export const VisibleIfSchema = z
  .union([
    z.tuple([z.string().uuid()]), // [string]
    z.tuple([]), // []
  ])
  .catch(captureAndReturn('VisibleIfSchema'))

export type VisibleIf = z.infer<typeof VisibleIfSchema>

// Shared display fields every AI source carries; per-type members add their own identifiers.
const AiSourceBaseSchema = z.object({
  sourceLabel: z.string(),
  quote: z.string().optional(),
  sourceUrl: z.string().optional(),
})

// 'document' covers user-uploaded files, scraped URLs, and (for the agentic fill-in) document chunks
// surfaced by RAG search — everything backed by a filestore document. `sourceType` stays optional on
// this member only: rows written before the union predate the discriminator.
const DocumentAiSourceSchema = AiSourceBaseSchema.extend({
  sourceType: z.literal('document').optional(),
  fileStoreId: z.string(),
  fileName: z.string().optional(),
  pageNumber: z.number().optional(),
  heading: z.string().optional(),
  sheetName: z.string().optional(),
})

// The remaining members are the agentic fill-in's non-document citation sources, mapped from the
// approval-agent citation union at persist time. Each carries the click-through identifiers of its
// source; the filestore-backed ones keep the preview affordance via `fileStoreId`.
const PolicyAiSourceSchema = AiSourceBaseSchema.extend({
  sourceType: z.literal('policy'),
  fileStoreId: z.string(),
  fileName: z.string().optional(),
  pageNumber: z.number().optional(),
  heading: z.string().optional(),
  policyId: z.string(),
  policyVersionId: z.string(),
})

const QuestionAiSourceSchema = AiSourceBaseSchema.extend({
  sourceType: z.literal('question'),
  requestFormId: z.string(),
  questionName: z.string(),
})

const OtherRequestFormAiSourceSchema = AiSourceBaseSchema.extend({
  sourceType: z.literal('other_request_form'),
  requestFormId: z.string(),
  questionName: z.string(),
})

const AgreementAiSourceSchema = AiSourceBaseSchema.extend({
  sourceType: z.literal('agreement'),
  agreementId: z.string(),
  supplierId: z.string(),
})

const TaskPrecedentAiSourceSchema = AiSourceBaseSchema.extend({
  sourceType: z.literal('task_precedent'),
  requestId: z.string(),
  requestStepId: z.string(),
})

const DocumentReviewFindingAiSourceSchema = AiSourceBaseSchema.extend({
  sourceType: z.literal('document_review_finding'),
  fileStoreId: z.string(),
  documentReviewId: z.string(),
})

// Plain z.union, not discriminatedUnion: the document member's `sourceType` is optional (legacy rows),
// which a discriminated union cannot express. Document goes first so those legacy rows match it.
const AiSourceInfoSchema = z.union([
  DocumentAiSourceSchema,
  PolicyAiSourceSchema,
  QuestionAiSourceSchema,
  OtherRequestFormAiSourceSchema,
  AgreementAiSourceSchema,
  TaskPrecedentAiSourceSchema,
  DocumentReviewFindingAiSourceSchema,
])
export type AiSourceInfo = z.infer<typeof AiSourceInfoSchema>

export const AiCompletionConfidenceSchema = z.enum(['high', 'medium', 'low'])
export type AiCompletionConfidence = z.infer<typeof AiCompletionConfidenceSchema>

const AiCompletionReferenceSchema = z.object({
  title: z.string(),
  url: z.string().url(),
})

const AiCompletionMetadataSchema = z
  .object({
    confidence: AiCompletionConfidenceSchema,
    reasoning: z.string(),
    references: z.array(AiCompletionReferenceSchema).optional(),
  })
  .openapi({
    description:
      'Confidence and reasoning for an answer submitted by the AI Form Completion block. Distinct from aiSources, which tracks document-extraction prefill citations.',
  })

export const FormAnswerMetadataSchema = z.object({
  isAiPrefilled: z.boolean().optional(),
  aiSources: z.array(AiSourceInfoSchema).optional(),
  aiCompletionMetadata: AiCompletionMetadataSchema.optional(),
})

export type FormAnswerMetadata = z.infer<typeof FormAnswerMetadataSchema>

export const BaseFormAnswerSchema = z.object({
  questionId: z.string().nullable(),
  metadata: FormAnswerMetadataSchema.optional(),
})
